import { MyMedicalPaOrdersTableData } from "../../../../data-model/tablesData";
import { MyMedicalPaOrdersState } from "../../myPaOrders/hooks/useMyPaOrdersTableData";
import { usePaginationReset } from "../../myPaOrders/hooks/usePaginationReset";
import { useFilterAndSearchEffect } from "./useFilterAndSearchEffect";
import { useFilterPayload } from "./useFilterPayload";
import { useSearchTerm } from "./useSearchTerm";
import { useValidFilters } from "./useValidFilters";

/**
 * Composes filter, search, and pagination hooks for the All Open Orders table.
 * Coordinates search term state, valid filters, filter payload generation,
 * and the side-effect that triggers data re-fetching on changes.
 *
 * @param initialDataFetchingExecuted - Whether the initial table data load has finished
 * @param setTableData - State setter for the transformed table row data
 * @param setAllOpenOrdersState - State setter for the orders loading/data/error state
 * @returns Filter payload, search term, pagination reset count, valid filters, and their setters
 */
export const useFilterAndSearch = (
  initialDataFetchingExecuted: boolean,
  setTableData: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersTableData[]>
  >,
  setAllOpenOrdersState: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersState>
  >,
) => {
  const { searchTerm, setSearchTerm } = useSearchTerm();
  const { paginationResetCount, setPaginationResetCount } =
    usePaginationReset();
  const validFilters = useValidFilters();
  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
  );

  useFilterAndSearchEffect(
    filterPayload,
    initialDataFetchingExecuted,
    setTableData,
    setAllOpenOrdersState,
    setPaginationResetCount,
  );

  return {
    filterPayload,
    searchTerm,
    setSearchTerm,
    paginationResetCount,
    setPaginationResetCount,
    validFilters,
  };
};
